import AppText, {
  generateAppTextOptionSetTranslationValue,
} from '@/entities/AppText';
import AppTranslation, {
  getUpdatedTranslation,
} from '@/entities/AppTranslation';
import Language from '@/entities/Language';
import { writeTranslations } from '@/utils/editor';
import googleTranslate from '@/utils/googleTranslate';
import React, { createContext, useContext, useState } from 'react';

interface AppTextsContextProps {
  appTexts: AppText[];
  setAppTexts: (appTexts: AppText[]) => void;
  updateAndWriteTranslationValues: (
    updates: {
      translation: AppTranslation;
      value: string;
    }[],
  ) => Promise<void>;
  googleTranslateAppText: (
    appText: AppText,
    mainLanguage: Language,
    onlyEmpty: boolean,
    googleApiKey?: string,
  ) => Promise<void>;
}

const AppTextsContext = createContext<AppTextsContextProps | undefined>(
  undefined,
);

export const AppTextsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [appTexts, setAppTexts] = useState<AppText[]>([]);

  const updateAndWriteTranslationValues = async (
    updates: { translation: AppTranslation; value: string }[],
  ) => {
    try {
      const updatedAppTexts = appTexts.map((appText) => {
        const matchingUpdate = updates.find(
          (update) => update.translation.appTextReference === appText.reference,
        );

        if (!matchingUpdate) {
          return appText;
        }

        const updatedTranslations = { ...appText.translations };
        updates.forEach(({ translation, value }) => {
          if (translation.appTextReference === appText.reference) {
            updatedTranslations[translation.languageCode] =
              getUpdatedTranslation(translation, value);
          }
        });

        return {
          ...appText,
          translations: updatedTranslations,
        };
      });

      //
      let toWriteTranslations: AppTranslation[] = [];

      updates.forEach(({ translation, value }) => {
        if (!translation.isOptionSet) {
          const updatedTranslation = getUpdatedTranslation(translation, value);
          toWriteTranslations.push(updatedTranslation);
        } else {
          if (
            !toWriteTranslations.find((t) => {
              return (
                t.appTextId === translation.appTextId &&
                t.languageCode === translation.languageCode
              );
            })
          ) {
            const optionSetValue = generateAppTextOptionSetTranslationValue(
              updatedAppTexts,
              translation.appTextId,
              translation.languageCode,
            );

            toWriteTranslations.push({
              reference: translation.id,
              id: translation.id,
              appTextId: translation.appTextId,
              appTextReference: translation.appTextReference,
              languageCode: translation.languageCode,
              value: optionSetValue,
              isOptionSet: false,
            });
          }
        }
      });

      await writeTranslations(toWriteTranslations);
      setAppTexts(updatedAppTexts);
    } catch (error) {
      console.error('Error updating translation value:', error);
    }
  };

  const googleTranslateAppText = async (
    appText: AppText,
    mainLanguage: Language,
    onlyEmpty: boolean,
    googleApiKey?: string,
  ): Promise<void> => {
    const source = appText.translations[mainLanguage.code];
    const updates = [];

    if (source?.value) {
      for (const [languageCode, translation] of Object.entries(
        appText.translations,
      )) {
        if (
          languageCode !== mainLanguage.code &&
          (!translation.value || !onlyEmpty) &&
          googleApiKey
        ) {
          const value = await googleTranslate(
            source.value,
            languageCode,
            googleApiKey,
          );

          if (value) {
            updates.push({
              translation,
              value,
            });
          }
        }
      }

      await updateAndWriteTranslationValues(updates);
    }
  };

  return (
    <AppTextsContext.Provider
      value={{
        appTexts,
        setAppTexts,
        updateAndWriteTranslationValues,
        googleTranslateAppText,
      }}
    >
      {children}
    </AppTextsContext.Provider>
  );
};

export const useAppTexts = (): AppTextsContextProps => {
  const context = useContext(AppTextsContext);
  if (!context) {
    throw new Error('useAppTexts must be used within a AppTextsProvider');
  }
  return context;
};
